import { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';

// Module-level cache: templateTagId -> Promise<partner | null>. A team realistically
// has 1-2 integrations, so each template -> partner chain is resolved once per page,
// not once per member row.
const templatePartnerCache = new Map();

async function resolveTemplatePartner(templateTagId, issuerId) {
  const db = firebase.firestore();
  const templateSnap = await db
    .collection('issuers').doc(issuerId)
    .collection('template_tags').doc(templateTagId)
    .get();
  if (!templateSnap.exists) return null;

  const template = templateSnap.data();
  const { partnerClientId } = template;
  if (!partnerClientId) return null;

  const clientSnap = await db.collection('clients').doc(partnerClientId).get();
  const client = clientSnap.exists ? clientSnap.data() : {};
  const partnerAvatar = client.avatar || client.topLogo || null;
  // The template's signup image is the mark a member actually recognises — it is
  // what they saw when they claimed the tag they are holding. The partner's own
  // avatar is the company logo, which may mean nothing to them. Prefer the
  // template; fall back to the partner rather than showing an empty circle.
  const signupImage = (template.actionText && template.actionText.signupImage) || null;
  return {
    partnerClientId,
    displayName: client.displayName || null,
    avatar: partnerAvatar,
    signupImage,
    brandImage: signupImage || partnerAvatar,
  };
}

/**
 * Resolve a template's branding (templateTagId -> template.partnerClientId ->
 * clients/{partnerClientId} avatar/topLogo + displayName), cached per templateTagId.
 *
 * `brandImage` is what to show: the template's signup image where set, the
 * partner's avatar otherwise.
 *
 * @returns {Promise<{partnerClientId, displayName, avatar, signupImage, brandImage} | null>}
 */
export function getTemplatePartner(templateTagId, issuerId = 'strike') {
  if (!templateTagId) return Promise.resolve(null);
  const cacheKey = `${issuerId}/${templateTagId}`;
  if (!templatePartnerCache.has(cacheKey)) {
    const promise = resolveTemplatePartner(templateTagId, issuerId).catch((err) => {
      console.warn(`getTemplatePartner: could not resolve ${cacheKey}:`, err);
      templatePartnerCache.delete(cacheKey); // allow a retry on the next call
      return null;
    });
    templatePartnerCache.set(cacheKey, promise);
  }
  return templatePartnerCache.get(cacheKey);
}

/**
 * Hook mapping an array of templateTagIds to their partner branding.
 *
 * @param {string[]} templateTagIds
 * @returns {Object} map of templateTagId -> {partnerClientId, displayName, avatar}
 *   (unresolved / partner-less templates are omitted)
 */
export function useTemplatePartners(templateTagIds, issuerId = 'strike') {
  const [partners, setPartners] = useState({});
  // Stable key so callers can pass a freshly-built array on every render.
  const key = (templateTagIds || []).filter(Boolean).sort().join(',');

  useEffect(() => {
    if (!key) {
      setPartners({});
      return undefined;
    }
    let cancelled = false;
    const ids = key.split(',');
    Promise.all(
      ids.map((id) => getTemplatePartner(id, issuerId).then((partner) => [id, partner])),
    ).then((entries) => {
      if (cancelled) return;
      const next = {};
      entries.forEach(([id, partner]) => {
        if (partner) next[id] = partner;
      });
      setPartners(next);
    });
    return () => { cancelled = true; };
  }, [key, issuerId]);

  return partners;
}
